import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../../components/button'
import RegisterAttribute from '../../components/registerAttribute'
import RegisterDocument from '../../components/registerDocument'
import TermsAndConditions from '../../components/termsAndConditions'
import Text from '../../components/text'
import { useAxios } from '../../core/AxiosContext'
import { useGlobal, useGlobalDispatch } from '../../core/GlobalContext'
import { REGISTER_DUCK, REGISTRATION_STATUS, REQUEST_STATUS, ROUTES, TOAST_DUCK } from '../../core/actions'
import { TRADECRED_SUPPORT_PHONE } from '../../core/constants'
import { camelToTitleCase } from '../../utils/misc'
import { registerSubmitParsers } from '../../utils/register/RegisterSubmitParsers'
import { validateRegisterAttributes } from '../../utils/validations'
import * as styles from './Register.css'
import { registerAction, uploadDocumentAction } from './RegisterDuck'
import { gstnKYC, registerOptions } from './registerOptions'
import { documentKeyToDoctypeMap, registerSubmitOptions } from './registerSubmitOptions'

const AccountType = ({ type, title, description }) => {
  const { registerDispatch } = useGlobalDispatch()

  return (
    <div className={styles.accountType}>
      <div className={styles.accountTypeWrapper}>
        <input
          type='radio'
          id={type}
          value={type}
          name={'accountType'}
          onClick={() => registerDispatch({ type: REGISTER_DUCK.ACCOUNT_TYPE, payload: { accountType: type } })}
        />
        <Text>{title}</Text>
      </div>
      <Text size='0.8' color='#808B96'>
        {description}
      </Text>
    </div>
  )
}

const GstnDetails = ({ kyc }) => {
  // TODO Dispatch has GSTN to attributes file
  return (
    <div className={styles.registrationDetails}>
      <Text className={styles.detailsWrapper} size='1.3' color='#273746' bold>
        {kyc?.attributesTitle}
      </Text>
      <Text size='0.8' color='#808B96'>
        {kyc?.attributesDescription}
      </Text>
      <div className={styles.attributesWrapper}>
        {kyc?.attributes?.map((attr, index) => (
          <RegisterAttribute key={index} attr={attr} />
        ))}
        {kyc?.documents?.map((attr, index) => (
          <RegisterDocument key={index} attr={attr} />
        ))}
      </div>
      <div className={styles.divider}></div>
    </div>
  )
}

const RegistrationDetails = ({ kyc }) => {
  return (
    <div className={styles.registrationDetails}>
      <Text className={styles.detailsWrapper} size='1.3' color='#273746' bold>
        {kyc?.attributesTitle}
      </Text>
      <Text size='0.8' color='#808B96'>
        {kyc?.attributesDescription}
      </Text>
      <div className={styles.attributesWrapper}>
        {kyc?.attributes?.map((attr, index) => (
          <RegisterAttribute key={index} attr={attr} />
        ))}
      </div>

      <div className={styles.halfdivider}></div>

      {kyc?.documents?.length > 0 && (
        <>
          <Text className={styles.uploadDetailsWrapper} size='1.3' color='#273746' bold>
            Upload your documents
          </Text>
          <Text size='0.8' color='#808B96'>
            Maximum file size is 5 MiB for each document or image
          </Text>
          <div className={styles.attributesWrapper}>
            {kyc?.documents?.map((attr, index) => (
              <RegisterDocument key={index} attr={attr} />
            ))}
          </div>

          <div className={styles.divider}></div>
        </>
      )}
    </div>
  )
}

export const Register = () => {
  const {
    registerState: {
      accountType,
      documents,
      message,
      reason,
      values,
      documentsUploadStatus,
      registrationStatus,
      availablePANs,
    },
  } = useGlobal()
  const { registerDispatch, toastDispatch } = useGlobalDispatch()
  const { publicAxios } = useAxios()

  const [selectedKYC, setSelectedKYC] = useState({})
  const [authorizedUserKYC, setAuthorizedUserKYC] = useState({})
  const [termsAndConditions, setTermsAndConditions] = useState(false)
  const [optinCommunication, setOptinCommunication] = useState(false)
  const [hasGSTN, setHasGSTN] = useState(false)
  const [docsRetryCount, setDocsRetryCount] = useState(0)
  const [showTNC, setShowTNC] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const navigate = useNavigate()
  const { referralCode } = useParams()

  const handleBackPress = () => navigate(ROUTES.LOGIN)
  // Update hasGSTN in Global Store
  const handleHasGSTN = e => {
    registerDispatch({ type: REGISTER_DUCK.VALUES_CHANGE, payload: { values: { hasGSTN: e.target.checked } } })
    setHasGSTN(e.target.checked)
  }
  // Update Terms and Conditions in Global Store
  const handleTNC = e => {
    registerDispatch({
      type: REGISTER_DUCK.VALUES_CHANGE,
      payload: { values: { termsAndConditions: e.target.checked } },
    })
    setTermsAndConditions(e.target.checked)
  }

  const handleOptinCommunication = e => {
    registerDispatch({
      type: REGISTER_DUCK.VALUES_CHANGE,
      payload: { values: { optinCommunication: e.target.checked } },
    })
    setOptinCommunication(e.target.checked)
  }

  const requiredKeysForRegistration = useCallback(() => {
    return hasGSTN
      ? [...registerSubmitOptions[accountType].required, ...registerSubmitOptions[accountType].requiredWithGstn]
      : registerSubmitOptions[accountType]?.required
  }, [hasGSTN, accountType])

  // Returns list of keys of docs that aren't yet uploaded
  const pendingUploadDocs = useCallback(
    () =>
      requiredKeysForRegistration()?.filter(camelCaseKey => {
        if (camelCaseKey.endsWith('Document')) {
          return !documents[camelCaseKey]?.id && camelCaseKey
        }
      }),
    [requiredKeysForRegistration, documents]
  )

  // Returns true if all required attributes have Success status
  const allRequiredAttributesSucceeded = useCallback(
    () =>
      requiredKeysForRegistration()
        ?.map(camelCaseKey => {
          if (camelCaseKey.endsWith('Document')) {
            return !!documents[camelCaseKey]?.id
          }
          return (
            message[camelCaseKey]?.status === 'SUCCESS' &&
            values[camelCaseKey] !== '' &&
            values[camelCaseKey] !== undefined
          )
        })
        .every(Boolean),
    [requiredKeysForRegistration, documents, message, values]
  )

  const requiredKeysWithFailureStatus = useCallback(
    () =>
      requiredKeysForRegistration()?.filter(key => {
        return message[key]?.status !== 'SUCCESS'
      }),
    [requiredKeysForRegistration, message]
  )

  const getDocsMissingIdsAndUpload = async () => {
    // Upload Pending Documents and save IDs to global state
    registerDispatch({ type: REGISTER_DUCK.UPLOADING_DOCS })
    await Promise.all(
      pendingUploadDocs().map(camelCaseKey => {
        if (document.getElementById(camelCaseKey)?.files[0]) {
          return uploadDocumentAction(
            document.getElementById(camelCaseKey)?.files[0],
            camelCaseKey,
            documentKeyToDoctypeMap[camelCaseKey],
            values?.pan
          )(publicAxios, registerDispatch)
        }
      })
    )
    registerDispatch({ type: REGISTER_DUCK.UPLOADED_DOCS })
  }

  const handleSubmit = async () => {
    requiredKeysForRegistration()?.forEach(key => {
      if (!key.endsWith('Document')) {
        validateRegisterAttributes(key, values[key])({ values, message, accountType, availablePANs }, registerDispatch)
      }
    })
    if (requiredKeysWithFailureStatus()?.length > 0) {
      setShowErrors(true)
    } else {
      setShowErrors(false)
      registerDispatch({ type: REGISTER_DUCK.REGISTER_STARTED })
      await getDocsMissingIdsAndUpload()
    }
  }

  // Validating every entry field on change
  useEffect(() => {
    Object.entries(values).map(([key, val]) => {
      validateRegisterAttributes(key, val)({ values, message, accountType, availablePANs }, registerDispatch)
    })
  }, [values, accountType, availablePANs])

  // If all upload document calls have completed and it still has documents to upload this will trigger
  // Resets docs retry count if there is no pending doc to upload
  useEffect(() => {
    if (docsRetryCount >= 0 && docsRetryCount < 3) {
      if (
        pendingUploadDocs()?.length > 0 &&
        registrationStatus === REGISTRATION_STATUS.REGISTRATION_STARTED &&
        documentsUploadStatus === REQUEST_STATUS.SUCCESS
      ) {
        console.log('Triggers dosc missing ids and upload', docsRetryCount)
        getDocsMissingIdsAndUpload()
        setDocsRetryCount(count => count - 1)
      }
    } else if (docsRetryCount >= 3) {
      // Trigger failure and show Submit Button
      registerDispatch({
        type: REGISTER_DUCK.REGISTER_FAILURE,
        payload: { reason: `${docsRetryCount} Uploading document failed, check network connection or try again later` },
      })
    }
  }, [registrationStatus, documentsUploadStatus, docsRetryCount, pendingUploadDocs])

  useEffect(() => {
    if (registrationStatus === REGISTRATION_STATUS.REGISTRATION_STARTED) {
      if (pendingUploadDocs()?.length === 0 && allRequiredAttributesSucceeded()) {
        console.log('Trigger Register Action')
        console.log(
          registerSubmitParsers(accountType, {
            values: values,
            documents: documents,
          })
        )
        registerAction(
          registerSubmitParsers(accountType, {
            values: values,
            documents: documents,
          })
        )(publicAxios, registerDispatch)
      } else {
        console.log('Recheck all fields', pendingUploadDocs())
      }
    }
  }, [registrationStatus, pendingUploadDocs, allRequiredAttributesSucceeded, requiredKeysWithFailureStatus])

  // On load extract referralCode and update Global State
  useEffect(() => {
    window.scrollTo(0, 0)

    if (referralCode) {
      registerDispatch({
        type: REGISTER_DUCK.VALUES_CHANGE,
        payload: { values: { referralCode: referralCode } },
      })
    }

    // Set hasGSTN and termsAndConditions to false initially
    registerDispatch({
      type: REGISTER_DUCK.VALUES_CHANGE,
      payload: { values: { hasGSTN: false, termsAndConditions: false, optinCommunication: false } },
    })
  }, [referralCode])

  useEffect(() => {
    // On Account Type change, cleanup unknown fields and retain similar filled fields
    const extractedKYC = registerOptions.filter(({ type }) => type === accountType)[0]
    setSelectedKYC(extractedKYC)
    extractedKYC?.authorizedUser && setAuthorizedUserKYC(extractedKYC.authorizedUser)
    setShowErrors(false)

    // Tidy up
    return () => {
      setAuthorizedUserKYC({})
    }
  }, [accountType])

  // All the toasts go here
  useEffect(() => {
    if (registrationStatus === REGISTRATION_STATUS.REGISTRATION_SUCCESS) {
      toastDispatch({
        type: TOAST_DUCK.SEND_TOAST,
        payload: {
          message: 'We have received your information, you will be contacted in 24-36 hours after verification',
          type: REQUEST_STATUS.SUCCESS,
        },
      })
      handleBackPress()
    } else if (registrationStatus === REGISTRATION_STATUS.REGISTRATION_FAILURE) {
      toastDispatch({
        type: TOAST_DUCK.SEND_TOAST,
        payload: {
          message: reason ? reason : 'Recheck all the fields again and provide all necessary information',
          type: REQUEST_STATUS.FAILURE,
        },
      })
    }
  }, [registrationStatus, reason])

  return (
    <div id='registerWrapper' className={styles.registerWrapper}>
      <div className={styles.register}>
        <div className={styles.header}>
          <div className={styles.headerWrapper}>
            <Button handleClick={handleBackPress} type='icon'>
              <ion-icon name='arrow-back-outline' />
            </Button>
            <Text size='1.7' bold>
              Create a new Income Bazaar account
            </Text>
          </div>
          <Text size='0.8' color='#808B96'>
            Stuck anywhere? Reach out to{' '}
            <a href={`tel:${TRADECRED_SUPPORT_PHONE}`} className={styles.specialText}>
              {TRADECRED_SUPPORT_PHONE}
            </a>
            , we will gladly help you out
          </Text>
        </div>
        <div className={styles.accountTypesWrapper}>
          <Text size='1.3' color='#273746' bold>
            Choose your Account Type
          </Text>
          <div className={styles.accountTypes}>
            {registerOptions
              .filter(({ type }) => type !== 'llp')
              .map(({ type, title, description }) => (
                <AccountType key={type} type={type} title={title} description={description} />
              ))}
          </div>
        </div>
        {accountType && (
          <>
            <RegistrationDetails kyc={selectedKYC} />
            <div className={styles.tAndC}>
              <input type='checkbox' checked={hasGSTN} onChange={handleHasGSTN} name='gstn' />
              <Text>I am registered with GST</Text>
            </div>
            {hasGSTN && <GstnDetails kyc={gstnKYC} />}
            {authorizedUserKYC?.type && <RegistrationDetails kyc={authorizedUserKYC} />}

            <div className={styles.tAndC}>
              <input type='checkbox' checked={termsAndConditions} onChange={handleTNC} name='termsAndConditions' />
              <div>
                <Text>I hereby agree to these</Text>
                <Button type='text' handleClick={() => setShowTNC(true)}>
                  <Text color='var(--ib-red)'>Terms & Conditions </Text>
                </Button>
                <Text>
                  and submit the above information to be used to create an account in Income Bazaar. I also confirm that
                  I have an annual income of more than INR 50 Lakhs
                </Text>
                <TermsAndConditions showTNC={showTNC} setShowTNC={setShowTNC} />
              </div>
            </div>
            <Text size='0.8' color='#808B96'>
              This is an invite only Platform. We shall verify your details and update you by email/sms.
            </Text>
            <div className={styles.tAndC}>
              <input
                type='checkbox'
                checked={optinCommunication}
                onChange={handleOptinCommunication}
                name='optinCommunication'
              />
              <div>
                <Text>
                  Opt-in to receive promotion and communication from Income Bazaar through Call, SMS and/or WhatsApp
                </Text>
              </div>
            </div>
            <Button
              className={styles.submitBtn}
              label={documentsUploadStatus === REQUEST_STATUS.PENDING ? 'Uploading ...' : 'Submit'}
              handleClick={handleSubmit}
              disabled={
                registrationStatus === REGISTRATION_STATUS.REGISTRATION_PENDING ||
                documentsUploadStatus === REQUEST_STATUS.PENDING ||
                requiredKeysWithFailureStatus()?.length > 0
              }></Button>

            <div className={styles.errorMessage}>
              {showErrors && requiredKeysWithFailureStatus()?.length > 0 && (
                <>
                  <Text>Check the following fields for errors — </Text>
                  {requiredKeysWithFailureStatus()?.map(key => {
                    return (
                      <Text
                        className={styles.errorMessageText}
                        key={key}
                        color='var(--register-attributes-message-failure)'>
                        {camelToTitleCase(key)}
                      </Text>
                    )
                  })}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
